/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

/**
 * Override this handler to pass includePriceBreakTable properties
 * Override this handler to pass proper expand with getVaraints call
 */

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { executeAwaitableHandlerChain, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getProductByPath } from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { recentlyViewedGetProductCollectionParameter } from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProductByPath from "@insite/client-framework/Store/Data/Products/Handlers/LoadProductByPath";
import loadVariantChildren from "@insite/client-framework/Store/Data/Products/Handlers/LoadVariantChildren";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    LoadProductIfNeeded,
    LoadRealTimePrices,
    RequestVariantChildrenFromApi,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = {
    path: string;
    styledOption?: string;
};
type Props = {
    product?: ProductModel;
    productInfosById?: SafeDictionary<ProductInfo>;
    variantChildren?: ProductModel[];
    variantChildrenProductInfos?: ProductInfo[];
    variantSelection?: SafeDictionary<string>;
    pricingLoaded?: true;
    inventoryLoaded?: true;
};

type HandlerType = Handler<Parameter, Props>;

export const CustomRequestVariantChildrenFromApi: HandlerType = async props => {
    if (!props.product?.variantTraits || props.product.variantTraits.length === 0) {
        return;
    }

    props.variantChildren = getVariantChildrenDataView(props.getState(), props.product.id).value;
    if (!props.variantChildren) {
        props.variantChildren = await executeAwaitableHandlerChain(
            loadVariantChildren as any,
            {
                productId: props.product.id,
                additionalExpands: ["getbadges"],
                expand: [
                    // <xng-change>
                    "detail",
                    "specifications",
                    "content",
                    "images",
                    "documents",
                    "attributes",
                    "properties",
                    // </xng-change>
                ],
                includeAttributes: ["includeOnProduct"],
            } as any,
            props,
        );
    }
};

export const CustomLoadRealTimePrices: HandlerType = async props => {
    if (!props.product || !props.productInfosById) {
        return;
    }

    const productInfo = props.productInfosById[props.product.id];
    if (!productInfo) {
        return;
    }
    const { productId } = productInfo;
    // <xng-change>
    const productData: any = {
        ...productInfo,
        properties: {
            includePriceBreakTable: "true",
        },
    };
    // </xng-change>
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: [productData], // <xng-change/>
            onComplete: pricingProps => {
                if (pricingProps.apiResult) {
                    const pricing = pricingProps.apiResult.realTimePricingResults!.find(o => o.productId === productId);
                    if (pricing) {
                        props.dispatch({
                            type: "Pages/ProductDetails/CompleteLoadRealTimePricing",
                            pricing,
                        });
                    } else {
                        props.dispatch({
                            type: "Pages/ProductDetails/FailedLoadRealTimePricing",
                            productId,
                        });
                    }
                } else if (pricingProps.error) {
                    props.dispatch({
                        type: "Pages/ProductDetails/FailedLoadRealTimePricing",
                        productId,
                    });
                }

                props.pricingLoaded = true;

                if (typeof window !== "undefined" && window !== null) {
                    const dataLayer = (window as any).dataLayer;
                    if (dataLayer) {
                        const data = {
                            event: "productDetails",
                            ecommerce: {
                                detail: {
                                    actionField: { list: "Product Detail Page", action: "detail" },
                                    products: [
                                        {
                                            name: props.product?.productTitle,
                                            id: props.product?.id,
                                            price: pricingProps.apiResult?.realTimePricingResults?.[0].unitListPrice,
                                            brand: props.product?.brand?.name || "",
                                            category: props.product?.urlSegment || "",
                                        },
                                    ],
                                },
                            },
                        };

                        dataLayer.push(data);
                    }
                }
            },
        }),
    );

    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const CustomLoadProductIfNeeded: typeof LoadProductIfNeeded = async props => {
    // if variantTypeId is not null this has variantTraits, and we need those loaded fully for this handler chain to work correctly
    // if product is discontinued then it may have replacement product, and we wait for it to be loaded
    if (!props.product || props.product.variantTypeId || props.product.isDiscontinued) {
        props.product = await executeAwaitableHandlerChain(
            loadProductByPath,
            { ...props.parameter, addToRecentlyViewed: true },
            props,
        );
    } else if (
        !props.product.detail ||
        !props.product.content ||
        !props.product.images ||
        !props.product.documents ||
        !props.product.specifications ||
        !props.product.attributeTypes ||
        !props.product.variantTraits
    ) {
        // we don't want to wait for this if we already partially have the product, because then we can't show the partially loaded product page
        props.dispatch(
            loadProductByPath({
                ...props.parameter,
                addToRecentlyViewed: true,
                onComplete: productProps => {
                    const configuration = productProps?.apiResult?.detail?.configuration;
                    if (configuration) {
                        props.dispatch({
                            type: "Pages/ProductDetails/InitConfigurationSelection",
                            configuration,
                        });
                    }

                    props.dispatch({
                        type: "Data/Products/ReplaceDataView",
                        parameter: recentlyViewedGetProductCollectionParameter,
                        dataView: {},
                    });
                },
            }),
        );
    } else {
        // <xng-change> : MIDPT-110: fixing an issue with not loading all properties of the products so we have missed data there
        getProductByPath({
            ...props.parameter,
            addToRecentlyViewed: true,
            expand: [
                "detail",
                "specifications",
                "content",
                "images",
                "documents",
                "attributes",
                "variantTraits",
                "properties",
            ],
            includeAttributes: ["includeOnProduct"],
        }).then(result => {
            props.dispatch({
                type: "Data/Products/CompleteLoadProduct",
                model: result,
            });
        });
    }
};

replaceInChain(chain, LoadRealTimePrices, CustomLoadRealTimePrices);
replaceInChain(chain, RequestVariantChildrenFromApi, CustomRequestVariantChildrenFromApi);
replaceInChain(chain, LoadProductIfNeeded, CustomLoadProductIfNeeded);
